import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from './useApiClient.ts';

// ---------------------------------------------------------------------------
// Shared types (mirrored from packages/api/src/handlers/end-consumers.ts)
// ---------------------------------------------------------------------------

export type EndConsumerStatus = 'invited' | 'active' | 'disabled';

export interface EndConsumerListItem {
  email: string;
  name: string | null;
  status: EndConsumerStatus;
  meterCount: number;
  lastLoginAt: string | null;
  sensorGroupId: number | null;
}

export interface ConsumerMeter {
  sensorId: number;
  deviceId: string;
  address: string | null;
  moveInDate: string | null;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/** Per-recipient outcome of an onboarding email send. */
export interface InviteEmailResult {
  email: string;
  sent: boolean;
  error?: string;
}

export interface AddEndConsumerResponse {
  email: string;
  sensorGroupId: number;
  meterCount: number;
  // Present only when sendInviteEmail was requested.
  inviteEmails?: InviteEmailResult[];
}

export function useEndConsumers(customerId: string | number | undefined) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const queryKey = ['end-consumers', customerId] as const;

  // -------------------------------------------------------------------------
  // Query
  // -------------------------------------------------------------------------

  const {
    data,
    isLoading,
    error,
  } = useQuery({
    queryKey,
    queryFn: () =>
      apiClient.get<{ consumers: EndConsumerListItem[] }>(
        `customers/${customerId}/end-consumers`,
      ),
    enabled: !!customerId,
  });

  // -------------------------------------------------------------------------
  // Mutations
  // -------------------------------------------------------------------------

  const addEndConsumerMutation = useMutation({
    mutationFn: (
      { email, meterIds, name, moveInDate, sendInviteEmail }: {
        email: string;
        meterIds: number[];
        name?: string;
        moveInDate?: string;
        sendInviteEmail?: boolean;
      },
    ) =>
      apiClient.post<AddEndConsumerResponse>(
        `customers/${customerId}/end-consumers`,
        { email, meterIds, name, moveInDate, sendInviteEmail },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const patchEndConsumerMutation = useMutation({
    mutationFn: ({
      email,
      patch,
    }: {
      email: string;
      patch: { email?: string; enabled?: boolean; name?: string };
    }) =>
      apiClient.patch<{ email: string }>(
        `customers/${customerId}/end-consumers/${encodeURIComponent(email)}`,
        patch,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const addEndConsumerMetersMutation = useMutation({
    mutationFn: ({ email, meterIds }: { email: string; meterIds: number[] }) =>
      apiClient.post<{ added: number }>(
        `customers/${customerId}/end-consumers/${encodeURIComponent(email)}/meters`,
        { meterIds },
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({
        queryKey: ['end-consumer-meters', customerId, variables.email],
      });
    },
  });

  const removeEndConsumerMeterMutation = useMutation({
    mutationFn: ({ email, sensorId }: { email: string; sensorId: number }) =>
      apiClient.delete<{ removed: boolean }>(
        `customers/${customerId}/end-consumers/${encodeURIComponent(email)}/meters/${sensorId}`,
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({
        queryKey: ['end-consumer-meters', customerId, variables.email],
      });
    },
  });

  const patchMeterMoveInMutation = useMutation({
    mutationFn: (
      { email, sensorId, moveInDate }: {
        email: string;
        sensorId: number;
        moveInDate: string | null;
      },
    ) =>
      apiClient.patch<{ moveInDate: string | null }>(
        `customers/${customerId}/end-consumers/${encodeURIComponent(email)}/meters/${sensorId}`,
        { moveInDate },
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({
        queryKey: ['end-consumer-meters', customerId, variables.email],
      });
    },
  });

  const removeEndConsumerMutation = useMutation({
    mutationFn: (email: string) =>
      apiClient.delete<{ revoked: boolean; deleted: boolean }>(
        `customers/${customerId}/end-consumers/${encodeURIComponent(email)}`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const sendInviteEmailMutation = useMutation({
    mutationFn: (email: string) =>
      apiClient.post<{ email: string; sent: boolean }>(
        `customers/${customerId}/end-consumers/${encodeURIComponent(email)}/invite-email`,
      ),
  });

  // -------------------------------------------------------------------------
  // Return
  // -------------------------------------------------------------------------

  return {
    consumers: data?.consumers ?? [],
    isLoading,
    error,
    addEndConsumer: addEndConsumerMutation.mutateAsync,
    sendInviteEmail: sendInviteEmailMutation.mutateAsync,
    patchEndConsumer: (
      email: string,
      patch: { email?: string; enabled?: boolean; name?: string },
    ) => patchEndConsumerMutation.mutateAsync({ email, patch }),
    addMeters: addEndConsumerMetersMutation.mutateAsync,
    removeMeter: removeEndConsumerMeterMutation.mutateAsync,
    patchMeterMoveIn: (email: string, sensorId: number, moveInDate: string | null) =>
      patchMeterMoveInMutation.mutateAsync({ email, sensorId, moveInDate }),
    removeEndConsumer: removeEndConsumerMutation.mutateAsync,
  };
}

// ---------------------------------------------------------------------------
// useConsumerMeters — per-consumer meter list
// ---------------------------------------------------------------------------

export function useConsumerMeters(
  customerId: string | number | undefined,
  email: string | undefined,
) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['end-consumer-meters', customerId, email] as const,
    queryFn: () =>
      apiClient.get<{ meters: ConsumerMeter[] }>(
        `customers/${customerId}/end-consumers/${encodeURIComponent(email!)}/meters`,
      ),
    enabled: !!customerId && !!email,
  });
}
